import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useWA, PageHead, Card, Btn, Field, Toggle, ImageInput, Skeleton, ErrorBox, Badge, cx } from "../ui";
import { siteLink } from "../api";

const SECTIONS = [
  ["general", "General & SEO", "fa-solid fa-house"],
  ["seo", "Google & SEO tools", "fa-brands fa-google"],
  ["contact", "Contact details", "fa-solid fa-address-card"],
  ["social", "Social links", "fa-solid fa-share-nodes"],
  ["app", "Android app (APK)", "fa-brands fa-android"],
  ["manager", "Admin Manager Program", "fa-solid fa-user-shield"],
  ["features", "Features ON/OFF", "fa-solid fa-toggle-on"],
  ["announcement", "Announcement bar", "fa-solid fa-bullhorn"],
  ["maintenance", "Maintenance mode", "fa-solid fa-screwdriver-wrench"],
];

const FEATURES = [
  ["signup", "नए sign up", "बंद करने पर कोई नया account नहीं बना पाएगा"],
  ["writing", "Blog लिखना / publish", "बंद करने पर users नए blog save/publish नहीं कर पाएँगे"],
  ["comments", "Comments", "Blogs पर नए comments"],
  ["likes", "Likes", "Blogs और posters पर like"],
  ["posters", "Poster upload", "Users नए posters डाल सकें"],
  ["payments", "नए payments", "Subscription page पर UPI payment form"],
  ["chatWidget", "AI chat बटन", "हर page पर नीचे दिखने वाला chat सहायक"],
  ["adminTab", "'Admin manager join' tab", "Page के दाईं तरफ़ दिखने वाली पट्टी"],
  ["translator", "भाषा बदलने का बटन (Translate)", "Login users को हर page पर तैरता हुआ बटन — पूरा page अपनी भाषा में पढ़ सकें"],
];

const SOCIAL = [
  ["facebook", "Facebook", "fa-brands fa-facebook", "https://facebook.com/…"],
  ["instagram", "Instagram", "fa-brands fa-instagram", "https://instagram.com/…"],
  ["youtube", "YouTube", "fa-brands fa-youtube", "https://youtube.com/@…"],
  ["x", "X (Twitter)", "fa-brands fa-x-twitter", "https://x.com/…"],
  ["telegram", "Telegram", "fa-brands fa-telegram", "https://t.me/…"],
  ["whatsapp", "WhatsApp Channel", "fa-brands fa-whatsapp", "https://whatsapp.com/channel/…"],
  ["playStore", "Play Store (app link)", "fa-brands fa-google-play", "https://play.google.com/store/apps/details?id=…"],
];

/* Google & SEO tools: health check + "tell search engines" button */
function SeoTools() {
  const { api, run, toast } = useWA();
  const [h, setH] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api("seoHealth").then(setH).catch(() => setH(null));
  }, [api]);
  const ping = async () => {
    setBusy(true);
    try {
      const r = await run("seoPing", { days: 30 });
      if (r && r.ok) toast(`${r.count} links search engines को भेज दिए`);
      else toast((r && r.reason) || `नहीं भेज पाए${r && r.status ? " (" + r.status + ")" : ""}`, "error");
    } catch (e) {
      /* toast */
    }
    setBusy(false);
  };
  const row = (n, ok, text, fix) => (
    <li className="flex items-start gap-3 py-2.5">
      <i className={cx("mt-0.5", ok ? "fa-solid fa-circle-check text-emerald-500" : "fa-solid fa-triangle-exclamation text-amber-500")} />
      <span className="flex-1 text-sm">
        {text} {!ok && <b>({n})</b>}
        {!ok && fix && <span className="block text-xs wa-muted">{fix}</span>}
      </span>
    </li>
  );
  const site = (h && h.site) || "https://ycnp.in";
  return (
    <div className="space-y-5 mt-6 pt-6 border-t wa-line">
      <div>
        <p className="font-bold">Blogs की SEO जाँच</p>
        {!h ? <Skeleton rows={3} h={28} /> : (
          <ul className="divide-y wa-line">
            {row(h.published, h.published > 0, `${h.published} published blogs`, "")}
            {row(h.noDesc, h.noDesc === 0, "हर blog में description है", "जिन blogs में description नहीं, Google खुद कुछ भी उठा लेता है — Blogs में जाकर लिखें")}
            {row(h.noFocus, h.noFocus === 0, "हर blog में focus keyword है", "Focus keyword से title, heading और पहले paragraph की जाँच होती है")}
            {row(h.noCover, h.noCover === 0, "हर blog में cover image है", "Cover image से Google Discover और share में बड़ा card दिखता है")}
            {row(h.noCat, h.noCat === 0, "हर blog किसी category में है", "Category से topic page मज़बूत होता है")}
            {row(h.thin, h.thin === 0, "कोई बहुत छोटा blog नहीं (500+ शब्द)", "बहुत छोटे articles कम rank करते हैं — इन्हें बढ़ाएँ")}
          </ul>
        )}
      </div>
      <div className="rounded-2xl border wa-line p-4">
        <p className="font-bold">Search engines को नए pages बताएँ</p>
        <p className="text-sm wa-muted mt-1">पिछले 30 दिन के नए/बदले blogs, posters और topic pages Bing, Yandex वगैरह को तुरंत भेजे जाते हैं (IndexNow)। Blog publish होने पर यह अपने-आप भी होता है।</p>
        <Btn variant="primary" icon="fa-solid fa-paper-plane" loading={busy} onClick={ping} className="mt-3">अभी भेजें</Btn>
      </div>
      <div className="rounded-2xl border wa-line p-4 text-sm space-y-2">
        <p className="font-bold">ज़रूरी links</p>
        <p><a className="underline" href="https://search.google.com/search-console" target="_blank" rel="noreferrer">Google Search Console</a> — site जोड़ें, फिर Sitemaps में <code className="select-all">{site}/sitemap.xml</code> डालें</p>
        <p><a className="underline" href="https://www.bing.com/webmasters" target="_blank" rel="noreferrer">Bing Webmaster Tools</a> — Search Console से site import कर सकते हैं</p>
        <p><a className="underline" href={site + "/sitemap.xml"} target="_blank" rel="noreferrer">Sitemap</a> · <a className="underline" href={site + "/feed.xml"} target="_blank" rel="noreferrer">RSS feed</a> · <a className="underline" href={site + "/robots.txt"} target="_blank" rel="noreferrer">robots.txt</a> · <a className="underline" href="https://search.google.com/test/rich-results" target="_blank" rel="noreferrer">Rich results test</a></p>
      </div>
    </div>
  );
}

export default function Settings() {
  const { api, run, confirm } = useWA();
  const loc = useLocation();
  const nav = useNavigate();
  const tab = new URLSearchParams(loc.search).get("tab") || "general";
  const [all, setAll] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [downloads, setDownloads] = useState(null);

  const load = () =>
    api("settingsGet")
      .then((r) => {
        setAll(r.settings);
        setDownloads(r.appDownloads ?? null);
      })
      .catch((e) => setError(e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (all) setForm({ ...all[tab], __tab: tab });
    setDirty(false);
  }, [all, tab]);

  const set = (k, val) => {
    setForm((x) => ({ ...x, [k]: val }));
    setDirty(true);
  };
  const go = async (k) => {
    if (dirty && !(await confirm({ title: "बदलाव save नहीं हुए", text: "बिना save किए आगे बढ़ें?", ok: "हाँ" }))) return;
    nav(`/website/settings?tab=${k}`);
  };
  const save = async () => {
    setBusy(true);
    try {
      const value = { ...v };
      delete value.__tab;
      const r = await run("settingsSave", { section: tab, value }, "Save हो गया — वेबसाइट पर कुछ सेकंड में दिखेगा");
      setAll((a) => ({ ...a, [tab]: r.value }));
    } catch (e) {
      /* toast */
    }
    setBusy(false);
  };
  const reset = async () => {
    if (!(await confirm({ title: "यह section पहले जैसा (default) करें?", danger: true, ok: "Reset" }))) return;
    try {
      await run("settingsReset", { section: tab }, "Default पर वापस");
      load();
    } catch (e) {
      /* toast */
    }
  };

  if (error) return <ErrorBox msg={error} retry={load} />;
  const v = form && form.__tab === tab ? form : null;
  const cur = SECTIONS.find((s) => s[0] === tab) || SECTIONS[0];

  return (
    <div>
      <PageHead icon="fa-solid fa-sliders" title="Site Settings" subtitle="बिना code बदले वेबसाइट की चीज़ें यहीं से बदलें" />
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5 items-start">
        <nav className="wa-card p-2 lg:sticky lg:top-20" aria-label="Settings sections">
          {SECTIONS.map(([k, l, i]) => (
            <button key={k} type="button" onClick={() => go(k)} className={cx("w-full flex items-center gap-3 px-3 h-11 rounded-xl text-[13.5px] text-left", tab === k ? "font-bold text-white" : "hover:bg-slate-500/10")} style={tab === k ? { background: "var(--wa-accent)" } : {}}>
              <i className={cx(i, "w-4 text-center")} />
              <span className="flex-1">{l}</span>
              {all && k === "maintenance" && all.maintenance.enabled && <Badge tone="amber">ON</Badge>}
              {all && k === "announcement" && all.announcement.enabled && <Badge tone="cyan">ON</Badge>}
              {all && k === "app" && !all.app.enabled && <Badge tone="amber">OFF</Badge>}
            </button>
          ))}
          <div className="border-t wa-line mt-2 pt-2">
            <Link to="/website/plans" className="w-full flex items-center gap-3 px-3 h-11 rounded-xl text-[13.5px] hover:bg-slate-500/10"><i className="fa-solid fa-crown w-4 text-center" />Plans & UPI QR</Link>
            <Link to="/website/pages" className="w-full flex items-center gap-3 px-3 h-11 rounded-xl text-[13.5px] hover:bg-slate-500/10"><i className="fa-regular fa-file-lines w-4 text-center" />About / Terms / Privacy</Link>
          </div>
        </nav>

        {!v ? <Skeleton rows={6} h={56} /> : (
          <Card title={cur[1]} icon={cur[2]} actions={<><Btn size="sm" onClick={reset}>Default</Btn><Btn variant="primary" icon="fa-solid fa-floppy-disk" loading={busy} onClick={save}>Save {dirty && "•"}</Btn></>}>
            {tab === "general" && (
              <div className="space-y-4 max-w-2xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Website का नाम"><input className="wa-input" value={v.siteName} onChange={(e) => set("siteName", e.target.value)} /></Field>
                  <Field label="Tagline"><input className="wa-input" value={v.tagline} onChange={(e) => set("tagline", e.target.value)} /></Field>
                </div>
                <ImageInput label="Logo" square value={v.logo} folder="brand" onChange={(x) => set("logo", x)} hint="Maintenance page पर दिखता है" />
                <Field label="Default SEO title" hint="Google और browser tab में (जिन pages का अपना title नहीं)" right={`${v.seoTitle.length}/70`}><input className="wa-input" value={v.seoTitle} onChange={(e) => set("seoTitle", e.target.value)} /></Field>
                <Field label="Default SEO description" right={`${v.seoDescription.length}/160`}><textarea rows={3} className="wa-input" value={v.seoDescription} onChange={(e) => set("seoDescription", e.target.value)} /></Field>
              </div>
            )}

            {tab === "contact" && (
              <div className="space-y-4 max-w-2xl">
                <p className="text-sm wa-muted">ये details Contact page, Subscription page और footer में दिखती हैं।</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Main email"><input className="wa-input" value={v.email} onChange={(e) => set("email", e.target.value)} /></Field>
                  <Field label="Support email"><input className="wa-input" value={v.supportEmail} onChange={(e) => set("supportEmail", e.target.value)} /></Field>
                  <Field label="Phone (दिखाने के लिए)"><input className="wa-input" value={v.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98765 43210" /></Field>
                  <Field label="WhatsApp number" hint="Country code के साथ, बिना + के: 919876543210"><input className="wa-input" value={v.whatsapp} onChange={(e) => set("whatsapp", e.target.value.replace(/\D/g, ""))} /></Field>
                </div>
                <Field label="पता"><textarea rows={2} className="wa-input" value={v.address} onChange={(e) => set("address", e.target.value)} /></Field>
                <Field label="Map के लिए जगह" hint="Google Map में यही खोजा जाएगा"><input className="wa-input" value={v.mapPlace} onChange={(e) => set("mapPlace", e.target.value)} /></Field>
              </div>
            )}

            {tab === "social" && (
              <div className="space-y-3 max-w-2xl">
                <p className="text-sm wa-muted">Footer में ये icons दिखेंगे। जो खाली हैं वो नहीं दिखेंगे।</p>
                {SOCIAL.map(([k, l, i, ph]) => (
                  <div key={k} className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl grid place-items-center shrink-0 text-lg" style={{ background: "color-mix(in srgb, var(--wa-ink) 6%, transparent)" }}><i className={i} /></span>
                    <Field label={l} className="flex-1"><input className="wa-input" value={v[k]} placeholder={ph} onChange={(e) => set(k, e.target.value)} /></Field>
                  </div>
                ))}
              </div>
            )}

            {tab === "app" && (
              <div className="space-y-4 max-w-2xl">
                <p className="text-sm wa-muted">
                  YCNP Play Store पर नहीं है, इसलिए वेबसाइट के हर page पर "Download app" बटन है। बटन दबाते ही नीचे दिए link से APK download होता है।
                  Computer वालों को QR code दिखता है, iPhone वालों को बताया जाता है कि app अभी Android के लिए है।
                </p>
                {downloads !== null && (
                  <div className="flex items-center gap-3 rounded-2xl border wa-line p-4">
                    <span className="w-11 h-11 rounded-xl grid place-items-center text-lg text-white" style={{ background: "#3DDC84" }}><i className="fa-brands fa-android" /></span>
                    <div>
                      <p className="text-2xl font-extrabold leading-none">{Number(downloads).toLocaleString("en-IN")}</p>
                      <p className="text-xs wa-muted mt-1">बार website से app download हुआ</p>
                    </div>
                  </div>
                )}
                <Toggle label="Download बटन चालू" desc="बंद करने पर बटन /download page पर ले जाएँगे और वहाँ लिखा होगा कि download जल्द वापस आएगा" checked={v.enabled} onChange={(x) => set("enabled", x)} />
                <Toggle label="नीचे कोने वाला 'Download app' बटन" desc="हर page पर नीचे बाईं तरफ़ तैरता हुआ बटन (बंद करने पर भी menu और footer में बटन रहेगा)" checked={v.floatingButton} onChange={(x) => set("floatingButton", x)} />
                <Field label="APK का link" hint={'APK file को website के public folder में ycnp.apk नाम से रखें, तो यहाँ "/ycnp.apk" ही रहने दें। या किसी और जगह का पूरा link डालें (https://…)'}>
                  <input className="wa-input" value={v.apkUrl} onChange={(e) => set("apkUrl", e.target.value.trim())} placeholder="/ycnp.apk" />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="Version"><input className="wa-input" value={v.version} onChange={(e) => set("version", e.target.value)} placeholder="1.0.5" /></Field>
                  <Field label="Size"><input className="wa-input" value={v.size} onChange={(e) => set("size", e.target.value)} placeholder="18 MB" /></Field>
                  <Field label="किस Android पर चलेगा"><input className="wa-input" value={v.minAndroid} onChange={(e) => set("minAndroid", e.target.value)} placeholder="Android 7.0 or newer" /></Field>
                </div>
                <Field label="What's new (हर line एक point)" hint="Download page पर दिखेगा। खाली छोड़ें तो नहीं दिखेगा।">
                  <textarea rows={4} className="wa-input" value={v.whatsNew} onChange={(e) => set("whatsNew", e.target.value)} placeholder={"Faster Watch & Earn\nNew Karma screen"} />
                </Field>
                <div className="flex flex-wrap gap-3">
                  <a href={siteLink("/download")} target="_blank" rel="noreferrer" className="text-sm font-semibold underline">Download page देखें ↗</a>
                  <a href={siteLink("/download/apk")} target="_blank" rel="noreferrer" className="text-sm font-semibold underline">Download test करें ↗</a>
                </div>
              </div>
            )}

            {tab === "seo" && (
              <div className="space-y-4 max-w-2xl">
                <p className="text-sm wa-muted">Home page का title/description "General & SEO" में है। यहाँ Google/Bing से जोड़ने वाली चीज़ें हैं।</p>
                <Field label="Google Search Console verification code" hint={'Search Console → "HTML tag" तरीका → content="…" के अंदर वाला code ही डालें'}>
                  <input className="wa-input" value={v.googleVerification} onChange={(e) => set("googleVerification", e.target.value.replace(/.*content=["']?([^"'\s>]+).*/i, "$1").trim())} placeholder="abc123XYZ…" />
                </Field>
                <Field label="Bing Webmaster verification code" hint={'Bing → "Meta tag" → content="…" वाला code'}>
                  <input className="wa-input" value={v.bingVerification} onChange={(e) => set("bingVerification", e.target.value.replace(/.*content=["']?([^"'\s>]+).*/i, "$1").trim())} />
                </Field>
                <Field label="Google Analytics ID" hint="G- से शुरू होने वाला ID (Analytics → Admin → Data streams)">
                  <input className="wa-input" value={v.gaId} onChange={(e) => set("gaId", e.target.value.trim().toUpperCase())} placeholder="G-XXXXXXXXXX" />
                </Field>
                <ImageInput label="Share image (link share करने पर दिखने वाली)" value={v.shareImage} folder="brand" onChange={(x) => set("shareImage", x)} hint="1200×630 size सबसे अच्छा। खाली छोड़ें तो YCNP का अपना card बनेगा।" />
                <Field label="Site के मुख्य keywords" hint="Comma से अलग करें">
                  <textarea rows={2} className="wa-input" value={v.keywords} onChange={(e) => set("keywords", e.target.value)} />
                </Field>
                <SeoTools />
              </div>
            )}

            {tab === "manager" && (
              <div className="space-y-4 max-w-2xl">
                <p className="text-sm wa-muted">ये बातें website के <a className="underline" href={siteLink("/admin-manager")} target="_blank" rel="noreferrer">/admin-manager</a> page और form पर दिखती हैं। UPI QR वही रहेगा जो Plans & UPI QR में लगा है।</p>
                <Toggle label="नई applications लें" desc="बंद करने पर form की जगह 'Applications are closed' दिखेगा" checked={v.enabled} onChange={(x) => set("enabled", x)} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Joining fee (₹)"><input type="number" min="0" className="wa-input" value={v.fee} onChange={(e) => set("fee", Math.max(0, Math.round(Number(e.target.value) || 0)))} /></Field>
                  <Field label="Training (दिन)"><input type="number" min="1" className="wa-input" value={v.trainingDays} onChange={(e) => set("trainingDays", Math.max(1, Math.round(Number(e.target.value) || 1)))} /></Field>
                </div>
                <Field label="Page पर सूचना (optional)" hint="जैसे: 'अगला training batch 15 नवंबर से' — खाली छोड़ें तो नहीं दिखेगा"><input className="wa-input" value={v.note} maxLength={200} onChange={(e) => set("note", e.target.value)} /></Field>
                <Link to="/website/managers" className="inline-flex items-center gap-2 text-sm font-semibold underline"><i className="fa-solid fa-user-shield" /> Applications देखें</Link>
              </div>
            )}

            {tab === "features" && (
              <div className="max-w-2xl divide-y wa-line">
                {FEATURES.map(([k, l, d]) => <Toggle key={k} label={l} desc={d} checked={v[k]} onChange={(x) => set(k, x)} />)}
              </div>
            )}

            {tab === "announcement" && (
              <div className="space-y-4 max-w-2xl">
                <Toggle label="Announcement bar दिखाएँ" desc="हर page के नीचे एक रंगीन पट्टी — offers, खबर या ज़रूरी सूचना के लिए" checked={v.enabled} onChange={(x) => set("enabled", x)} />
                <Field label="Text"><input className="wa-input" value={v.text} maxLength={200} onChange={(e) => set("text", e.target.value)} placeholder="🎉 Diwali offer: सभी plans पर 30% छूट!" /></Field>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_110px] gap-3">
                  <Field label="Link (optional)"><input className="wa-input" value={v.link} onChange={(e) => set("link", e.target.value)} placeholder="/subscription" /></Field>
                  <Field label="Button text"><input className="wa-input" value={v.linkText} onChange={(e) => set("linkText", e.target.value)} placeholder="अभी देखें" /></Field>
                  <Field label="रंग"><input type="color" className="wa-input !p-1" value={v.color} onChange={(e) => set("color", e.target.value)} /></Field>
                </div>
                <div>
                  <p className="text-[12.5px] font-semibold mb-1.5">Preview</p>
                  <div className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-white" style={{ background: v.color }}>
                    <span>📢</span><p className="flex-1">{v.text || "आपका message यहाँ…"}</p>
                    {v.link && <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-extrabold">{v.linkText || "Open"}</span>}
                    <span className="w-6 h-6 rounded-full bg-black/20 grid place-items-center text-xs">✕</span>
                  </div>
                </div>
              </div>
            )}

            {tab === "maintenance" && (
              <div className="space-y-4 max-w-2xl">
                <div className={cx("rounded-2xl p-4 border", v.enabled ? "border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30" : "wa-line")}>
                  <Toggle label="Maintenance mode" desc="चालू करने पर आम लोगों को पूरी वेबसाइट की जगह सिर्फ नीचे वाला संदेश दिखेगा। Admin emails (ADMIN_EMAILS) से login लोग साइट देख पाएँगे। App पर कोई असर नहीं।" checked={v.enabled} onChange={(x) => set("enabled", x)} />
                </div>
                <Field label="Heading"><input className="wa-input" value={v.title} onChange={(e) => set("title", e.target.value)} /></Field>
                <Field label="Message"><textarea rows={4} className="wa-input" value={v.message} onChange={(e) => set("message", e.target.value)} /></Field>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mt-6 pt-4 border-t wa-line">
              <Btn variant="primary" icon="fa-solid fa-floppy-disk" loading={busy} onClick={save}>Save करें</Btn>
              {dirty && <span className="text-xs text-amber-600 font-semibold">बदलाव save नहीं हुए</span>}
              <a href={siteLink("/")} target="_blank" rel="noreferrer" className="ml-auto text-xs underline wa-muted">Website पर जाँचें ↗</a>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
